import { type SubscriptionTier } from '@heidi/schema/ml-scribe';

import { useQueryUserBillingInfo } from '../../data/query/useQueryUserBillingInfo';
import { isClinicianMaxBillingSeat } from './is-clinician-max-billing-seat';
import {
  isCreditUsageSurfaceEligible,
  isCreditUsageSurfaceEnabled,
  isHeidi2OptInCreditUsageEnabled,
} from './is-credit-usage-surface-enabled';
import { isMaxBillingSeat } from './is-max-billing-seat';
import { useTeamMaxUpgradeState } from './use-team-max-upgrade-state';

export type CreditUsageSurfaceFlags = {
  /** `release-heidi-2-opt-in` (app-resolved) — rollout switch for the Heidi 2 arm. */
  isHeidi2OptInEnabled: boolean;
};

/** Opt-in flag AND cached billing `heidi_version === 2`. */
export function useIsCreditUsageSurfaceEnabled({
  isHeidi2OptInEnabled,
}: CreditUsageSurfaceFlags): boolean {
  const { data: billingInfo } = useQueryUserBillingInfo();
  return isCreditUsageSurfaceEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
}

/** The surface flag AND a resolved tier, so a caller needs no billing read of its own. */
export function useIsCreditUsageSurfaceEligible({
  isHeidi2OptInEnabled,
  newUserTier,
}: CreditUsageSurfaceFlags & {
  newUserTier: SubscriptionTier | null | undefined;
}): boolean {
  const { data: billingInfo } = useQueryUserBillingInfo();
  return isCreditUsageSurfaceEligible({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
    newUserTier,
  });
}

/**
 * The sidebar-footer verdict. Owns the billing read so the sidebar slot needs
 * no billing hook of its own.
 *
 * A Clinician Max seat has nothing to upgrade to until Practice Max checkout
 * is live, so the tile stays hidden for that seat while
 * `release-heidi-2-practice-max-upgrade` is off. Practice Max is the top seat,
 * so a member on it has nothing left to buy and gets no tile. A Practice admin
 * (team upgrades on) is judged by the team instead: the tile hides once every
 * member is on, or scheduled for, Max, and an admin on a Max seat themselves
 * only gets it once members are known to be left. Everyone else follows opt-in
 * credit usage, on any tier.
 */
export function useShouldRenderSidebarCreditUsagePopover({
  isHeidi2OptInEnabled,
  isPracticeMaxUpgradeEnabled,
  isTeamUpgradesEnabled = false,
}: CreditUsageSurfaceFlags & {
  /** `release-heidi-2-practice-max-upgrade` (app-resolved, PostHog). */
  isPracticeMaxUpgradeEnabled: boolean;
  /** `release-heidi-2-team-upgrades` (app-resolved, PostHog). */
  isTeamUpgradesEnabled?: boolean;
}): boolean {
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { isTeamAdminUpgrade, hasMembersLeftToUpgrade } = useTeamMaxUpgradeState({
    isTeamUpgradesEnabled,
  });

  const isClinicianMax = isClinicianMaxBillingSeat(billingInfo);
  const isPracticeMax = isMaxBillingSeat(billingInfo) && !isClinicianMax;
  if (!isPracticeMaxUpgradeEnabled && isClinicianMax) {
    return false;
  }
  if (isTeamAdminUpgrade) {
    if (hasMembersLeftToUpgrade === false) return false;
    if (isPracticeMax && hasMembersLeftToUpgrade !== true) return false;
  } else if (isPracticeMax) {
    return false;
  }

  return isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
}
